import { useParams } from 'react-router-dom'
import {
  useFurnitureTypes,
  useProcotol,
  useProtocolFurniture
} from '@services/protocols/protocols.hook'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Stack,
  Alert
} from '@mui/material'
import { DeleteForever } from '@mui/icons-material'
import { usePersistedFormik } from '../hooks/usePersistedFormik'
import * as Yup from 'yup'
import { PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import { getProtocols } from '@services/protocols/protocols.helpers'
import AutoSaveIndicator, { AutoSaveStyles } from './AutoSaveIndicator'

function ProtocolFurnitures({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { furnitureTypes } = useFurnitureTypes()
  const { protocol } = useProcotol(Number(id))
  const { currentProtocol, oppositeProtocol } = getProtocols(protocol)

  const { protocolFurniture, createProtocolFurniture, deleteProtocolFurniture } =
    useProtocolFurniture(Number(currentProtocol?.id))

  const { protocolFurniture: oppositeProtocolFurniture } = useProtocolFurniture(
    Number(oppositeProtocol?.id)
  )

  const furnitureCreateform = usePersistedFormik(
    {
      validateOnMount: false,
      validateOnChange: false,
      initialValues: {
        furnitureType: 1,
        quantity: 1
      },
      validationSchema: Yup.object().shape({
        furnitureType: Yup.number().required(),
        quantity: Yup.number().required()
      }),
      onSubmit: async (values: { furnitureType: number; quantity: number }) => {
        await createProtocolFurniture.mutateAsync({
          furnitureType: values.furnitureType,
          quantity: values.quantity
        })
        furnitureCreateform.resetForm()
      }
    },
    {
      formType: 'furniture',
      protocolId: Number(currentProtocol?.id || id),
      autoSaveInterval: 3000,
      showNotifications: false, // Less intrusive for furniture forms
      clearOnSubmit: true
    }
  )

  const getFurnitureTypeName = (id: number) =>
    furnitureTypes.find((furnitureType) => furnitureType.id === id)?.name ?? id

  const removeProtocolFurniture = async (furnitureId: number) => {
    furnitureCreateform.setSubmitting(true)
    await deleteProtocolFurniture.mutateAsync(furnitureId)
    furnitureCreateform.setSubmitting(false)
  }

  const filteredSelectorTypes = furnitureTypes.filter(
    (item) =>
      !protocolFurniture.find((pItem) => getFurnitureTypeName(pItem.furnitureType) === item.name)
  )

  return (
    <Box>
      {/* Unsaved changes indicator */}
      {furnitureCreateform.hasUnsavedChanges && (
        <Alert
          severity='warning'
          sx={{ mb: 2 }}
          action={
            <Button size='small' onClick={furnitureCreateform.restoreFromSaved} sx={{ mr: 1 }}>
              Odrzuć zmiany
            </Button>
          }
        >
          Formularz zawiera niezapisane zmiany
        </Alert>
      )}

      {protocolFurniture.length === 0 && oppositeProtocolFurniture.length === 0 ? (
        <Typography variant='caption' fontWeight={700} display='block' mb={4}>
          Brak mebli
        </Typography>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: '100%' }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell align='right'>Ilość</TableCell>
                {!isReadOnly && <TableCell align='right'></TableCell>}
              </TableRow>
            </TableHead>
            {currentProtocol?.protocolType === PROTOCOL_TYPE.acceptance ? (
              <TableBody>
                {protocolFurniture.map((furniture) => (
                  <TableRow
                    key={furniture.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component='th' scope='row'>
                      {getFurnitureTypeName(furniture.furnitureType)}
                    </TableCell>
                    <TableCell align='right'> {furniture.quantity}</TableCell>
                    {!isReadOnly && (
                      <TableCell align='right'>
                        <IconButton
                          disabled={furnitureCreateform.isSubmitting}
                          onClick={() => removeProtocolFurniture(furniture.id)}
                        >
                          <DeleteForever />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <>
                  {oppositeProtocolFurniture.map((furniture) => (
                    <TableRow
                      key={furniture.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {getFurnitureTypeName(furniture.furnitureType)}
                      </TableCell>
                      <TableCell align='right'> {furniture.quantity}</TableCell>
                      {!isReadOnly && (
                        <TableCell align='right'>
                          <IconButton
                            disabled={furnitureCreateform.isSubmitting}
                            onClick={() => removeProtocolFurniture(furniture.id)}
                          ></IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {protocolFurniture.map((furniture) => (
                    <TableRow
                      key={furniture.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {getFurnitureTypeName(furniture.furnitureType)}
                      </TableCell>
                      <TableCell align='right'> {furniture.quantity}</TableCell>
                      {!isReadOnly && (
                        <TableCell align='right'>
                          <IconButton
                            disabled={furnitureCreateform.isSubmitting}
                            onClick={() => removeProtocolFurniture(furniture.id)}
                          >
                            <DeleteForever />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}

      {!isReadOnly && !!filteredSelectorTypes.length && (
        <>
          <Typography variant='caption' fontWeight={700}>
            Dodaj mebel
          </Typography>
          <form onSubmit={furnitureCreateform.handleSubmit}>
            <FormControl
              fullWidth
              sx={{ my: 2 }}
              error={!!furnitureCreateform.errors.furnitureType}
            >
              <InputLabel id='furnitureType'>Typ mebla</InputLabel>
              <Select
                disabled={furnitureCreateform.isSubmitting}
                labelId='furnitureType'
                value={furnitureCreateform.values.furnitureType}
                label='furnitureType'
                onChange={({ target }) =>
                  furnitureCreateform.setFieldValue('furnitureType', target.value)
                }
              >
                {filteredSelectorTypes.map((furnitureType) => (
                  <MenuItem key={furnitureType.id} value={furnitureType.id}>
                    {furnitureType.name}
                  </MenuItem>
                ))}
              </Select>
              {!!furnitureCreateform.errors.furnitureType && (
                <FormHelperText>{String(furnitureCreateform.errors.furnitureType)}</FormHelperText>
              )}
            </FormControl>
            <TextField
              error={!!furnitureCreateform.errors.quantity}
              helperText={
                furnitureCreateform.errors.quantity
                  ? String(furnitureCreateform.errors.quantity)
                  : undefined
              }
              fullWidth
              sx={{ my: 2 }}
              id='quantity'
              label='Ilość'
              variant='outlined'
              onChange={furnitureCreateform.handleChange}
              value={furnitureCreateform.values.quantity}
              disabled={furnitureCreateform.isSubmitting}
            />
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mt: 2 }}
            >
              <AutoSaveIndicator
                isDirty={furnitureCreateform.dirty && !furnitureCreateform.hasUnsavedChanges}
                isAutoSaving={false}
                lastSavedTime={furnitureCreateform.lastSaved}
              />
              <Button type='submit' variant='contained' loading={furnitureCreateform.isSubmitting}>
                Dodaj
              </Button>
            </Stack>
          </form>
        </>
      )}

      <style>{AutoSaveStyles}</style>
    </Box>
  )
}

export default ProtocolFurnitures
